import { Grid, Stack, Typography } from "@mui/material";
import { AquariusBadge } from "@smartrr/shared/customerPortal/components/AquariusBadge";
import { AquariusCard } from "@smartrr/shared/customerPortal/components/AquariusCard";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { LoyaltyApi } from "@smartrr/shared/interfaces/loyalty/api";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { useVariantIdToVariantMap } from "@smartrr/shared/utils/useVariantToPurchasableMap";
import { isVariantInStock } from "@smartrr/shared/utils/variants";
import { Question } from "akar-icons";
import capitalize from 'lodash/capitalize';
import React, { useCallback, useMemo, useRef } from "react";

import { useMobileQuery } from "@customer-app/account/views/OperationAquarius/utils";
import { useOpenModal } from "@customer-app/state/actionCreators/modal";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

import { LoyaltyStoreAccess } from "../../store";
import { IncentiveCard } from "../IncentiveCard";
import { VariantCard } from "../VariantCard";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { isRewardIncentive } from "../../utils";
import { useShowActiveSubscriberMultiplier } from "../../utils/useShowActiveSubscriberMultiplier";

export const RewardsList = () => {
  const incentives = LoyaltyStoreAccess.useIncentives();
  const variants = LoyaltyStoreAccess.useVariants();

  if (incentives.length === 0 && variants.length === 0) {
    return null;
  }

  return <RewardsListContainer incentives={incentives} variants={variants} />;
};

interface RewardsListContainerProps {
  incentives: LoyaltyApi.Program.Type["incentives"];
  variants: LoyaltyApi.Program.Type["variants"];
}

const RewardsListContainer = ({ incentives, variants }: RewardsListContainerProps) => {
  const { cumulativePoints: points } = LoyaltyStoreAccess.usePoints();
  const pointsSuffix = LoyaltyStoreAccess.useSuffix(points);
  const { singularUnit } = LoyaltyStoreAccess.useRewardUnits();
  const isLoading = LoyaltyStoreAccess.useIsLoading();
  const isTiersEnabled = LoyaltyStoreAccess.useIsTiersEnabled();
  const { currentTier } = LoyaltyStoreAccess.useTiers();
  const openModal = useOpenModal();
  const activeSubscriberMultipler = LoyaltyStoreAccess.useActiveSubscriberMultiplier();
  // unit name is always in plural
  const unitName = LoyaltyStoreAccess.useSuffix(2);
  const showMultiplier = useShowActiveSubscriberMultiplier();

  const purchasables = useSmartrrCustomerSelector(state => state.purchasables.purchasables);
  const variantMap = useVariantIdToVariantMap(purchasables);

  const isMobile = useMobileQuery();

  const stackRef = useRef<HTMLDivElement>(null);

  const isTiersAvailableToUser = useMemo(() => isTiersEnabled && !!currentTier, [isTiersEnabled, currentTier]);

  const openGetPointsModal = useCallback(
    () =>
      openModal("HOW_TO_EARN_POINTS", {
        title: typedLocalize("loyalty.getPoints", {
          unitName: pointsSuffix,
        }),
      }),
    []
  );

  const checkVariantInStock = (id: string) => {
    const variant = variantMap[id];
    return isVariantInStock(variant);
  };

  const incentivesAndVariants = useMemo(
    () =>
      [
        ...incentives,
        ...variants.filter(vnt => {
          return checkVariantInStock(vnt.variantId);
        }),
      ].sort((a, b) => {
        return a.costInPoints - b.costInPoints;
      }),
    [incentives, variants]
  );

  return (
    <Grid
      item
      xs={12}
      md={8}
      sx={{
        transition: ".3s ease-in-out",
      }}
      className={prefixClassName("rewardslist-container")}
    >
      <AquariusCard sx={{ height: "100%", padding: "20px 0px 5px 20px !important" }}>
        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: stackRef.current?.clientHeight }}>
            <DotSpinner />
          </Stack>
        ) : (
          <Stack
            ref={stackRef}
            direction="column"
            alignItems="center"
            spacing="25px"
            className={prefixClassName("loyalty-marketplace-balance-wrapper")}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: "100%", paddingRight: "20px" }}
            >
              <Stack direction="row" spacing="7px" alignItems="center">
                <Typography
                  variant="h4"
                  style={{ marginBottom: "0px" }}
                  className={prefixClassName("loyalty-marketplace-balance")}
                >
                  {typedLocalize("loyalty.pointBalance", {
                    unitName: capitalize(singularUnit),
                  })}
                </Typography>
                <AquariusBadge
                  icon="trophy"
                  className={prefixClassName("loyalty-marketplace-balance-badge")}
                  label={String(points.toLocaleString(navigator.language).replaceAll(/\s/g, "") ?? 0)}
                  sx={{
                    minHeight: "32px",
                    minWidth: "66px",
                    fontSize: "20px !important",
                    svg: {
                      minHeight: "18px",
                      minWidth: "18px",
                    },
                  }}
                />
              </Stack>
              {isTiersAvailableToUser || isMobile ? (
                <React.Fragment />
              ) : (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing="7px"
                  sx={{ cursor: "pointer" }}
                  onClick={() => openGetPointsModal()}
                  className={prefixClassName("loyalty-how-to-earn-points")}
                  data-testid="loyalty-how-to-earn-points"
                >
                  <Typography variant="caption1">
                    {showMultiplier
                      ? typedLocalize("loyalty.activeSubscriberMultiplier.earning", {
                          multiplier: activeSubscriberMultipler,
                          unitName,
                        })
                      : typedLocalize("loyalty.getPoints", {
                          unitName: pointsSuffix,
                        })}
                  </Typography>
                  <Question strokeWidth={2} size={16} />
                </Stack>
              )}
            </Stack>
            <Stack
              direction="row"
              spacing="20px"
              sx={{ paddingBottom: "15px", overflowX: "auto", overflowY: "hidden", width: "100%" }}
              id="loyalty-marketplace-rewards-list"
            >
              {incentivesAndVariants.map((item, index) => {
                const RewardItemCard = isRewardIncentive(item) ? (
                  <IncentiveCard incentive={item} />
                ) : (
                  <VariantCard variant={item} />
                );
                return (
                  <Stack
                    direction="column"
                    spacing="7px"
                    sx={{ maxWidth: "90px" }}
                    key={index}
                    id={`loyalty-reward-item-${item.id}`}
                  >
                    {RewardItemCard}
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        )}
      </AquariusCard>
    </Grid>
  );
};
